import type { ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useFocusTrap } from '../hooks/useFocusTrap.js';
import { lockBodyScroll } from '../lib/bodyScrollLock.js';
import { getModalPortalHost } from '../lib/modalPortal.js';
import { Icon } from './Icon.js';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
  fullscreen?: boolean;
  absolute?: boolean;
  className?: string;
  headerEnd?: ReactNode;
}

/** Accessible modal dialog: focus trap, Escape-to-close, backdrop dismissal. */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  closeLabel = 'Close',
  fullscreen = false,
  absolute = false,
  className,
  headerEnd,
}: ModalProps) {
  const trapRef = useFocusTrap(open, onClose);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    return lockBodyScroll({ allowTouchMoveWithin: scrollAreaRef.current });
  }, [open]);

  const content = (
    <AnimatePresence>
      {open && (
        <motion.div
          className={`overlay${absolute ? ' overlay-absolute' : ''}`}
          onPointerDown={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            ref={trapRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            className={`modal${fullscreen ? ` modal-full${absolute ? ' modal-full-absolute' : ''}` : ''}${className ? ` ${className}` : ''}`}
            onPointerDown={(event) => event.stopPropagation()}
            initial={fullscreen ? { opacity: 0 } : { y: '100%', opacity: 0 }}
            animate={fullscreen ? { opacity: 1 } : { y: 0, opacity: 1 }}
            exit={fullscreen ? { opacity: 0 } : { y: '100%', opacity: 0 }}
            transition={
              fullscreen ? { duration: 0.2 } : { type: 'spring', bounce: 0.18, duration: 0.4 }
            }
          >
            {(title || closeLabel) && (
              <header className="modal-head">
                {title ? (
                  <h2 id={titleId} className="modal-title">
                    {title}
                  </h2>
                ) : (
                  <span />
                )}
                <div className="modal-head-actions">
                  {headerEnd}
                  <button
                    type="button"
                    className="icon-btn modal-close-btn"
                    onClick={onClose}
                    aria-label={closeLabel}
                  >
                    <Icon name="close" />
                  </button>
                </div>
              </header>
            )}
            <div ref={scrollAreaRef} className="modal-body">
              {children}
            </div>
            {footer && <footer className="modal-foot">{footer}</footer>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (absolute || typeof document === 'undefined') return content;
  return createPortal(content, getModalPortalHost(document));
}
